import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { DEFAULT_SCENE_MAX_IMAGE_COUNT, MAX_IMG2V_FRAMES, MODEL_VERSION, RESOLUTION_MAP } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormImagePrompt, FormTabType, UploadImageType } from '@/app/types';
import { BackfillTaskType } from '@/app/types';
import { getMaxImageAndElementCount, getMaxImageSize, getNextAvailableImageIdx, getNextImageName } from '@/app/utils';
import { getBackfillImageId, openOrRedirect, resizeAndUploadImage, safeStorage, uploadFile } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { useImg2VideoBackfill } from './use-img2video-backfill';
import { useMentionablePrompts } from './use-mentionable-prompts';

const WEBP_BACKFILL_QUALITY = 0.95; // 回填图片专用质量设置

export const useBackfill = ({ openInNewTab = false }: { openInNewTab?: boolean } = {}) => {
  const [taskType, setTaskType] = useState<Exclude<FormTabType, 'scene'> | ''>('');
  const [scene, setScene] = useState<string>('');
  const pathname = usePathname();
  const t = useTranslations();

  const { handleBackfillImagePrompt } = useMentionablePrompts('character2video'); // 回填图片（仅在参考生2.0及以上中使用）
  const { handleBackfillImagePrompt: handleBackfillImagePromptReference2Image } =
    useMentionablePrompts('reference2image'); // 回填图片（仅在参考生2.0及以上中使用）
  const {
    character2videoFormParams,
    img2videoFormParams,
    sceneFormParams,
    reference2imageFormParams,
    styleTransferFormParams,
    faceSwapFormParams,
    motionControlFormParams,
    storyGridFormParams,
    updateTemplateFormParams,
    updateStyleTransferFormParams,
    updateFaceSwapFormParams,
    updateMotionControlFormParams,
    updateFormParams,
    setChar2VideoWarningTipVisible,
    isProMode,
  } = useStore((state) => ({
    character2videoFormParams: state.character2videoFormParams,
    img2videoFormParams: state.img2videoFormParams,
    reference2imageFormParams: state.reference2imageFormParams,
    styleTransferFormParams: state.styleTransferFormParams,
    faceSwapFormParams: state.faceSwapFormParams,
    motionControlFormParams: state.motionControlFormParams,
    storyGridFormParams: state.storyGridFormParams,
    sceneFormParams: state.sceneFormParams,
    updateTemplateFormParams: state.updateTemplateFormParams,
    updateStyleTransferFormParams: state.updateStyleTransferFormParams,
    updateFaceSwapFormParams: state.updateFaceSwapFormParams,
    updateMotionControlFormParams: state.updateMotionControlFormParams,
    updateFormParams: state.updateFormParams,
    setChar2VideoWarningTipVisible: state.setChar2VideoWarningTipVisible,
    isProMode: state.character2videoFormParams.isProMode,
  }));

  const { modelVersion: character2videoModelVersion, referencePrompts: character2videoReferencePrompts } =
    character2videoFormParams;

  const locale = useLocale();

  const { referencePrompts: reference2imageReferencePrompts } = reference2imageFormParams;

  const { imagePrompts: img2videoImagePrompts, modelVersion: img2videoModelVersion } = img2videoFormParams;

  const maxImageCount = img2videoModelVersion === MODEL_VERSION.Q3 ? 2 : MAX_IMG2V_FRAMES; // Q3 不支持智能多帧

  const { handleBackfillImagePromptToImg2Video } = useImg2VideoBackfill();

  const router = useRouter();

  // 获取回填坑位数量
  const getBackfillAvailable = (backfillTaskType: BackfillTaskType) => {
    // 新标签页跳转场景不受当前页面坑位限制
    if (openInNewTab) {
      return true;
    }

    let count = 0;

    switch (backfillTaskType) {
      case BackfillTaskType.CHARACTER2VIDEO: {
        count =
          getMaxImageAndElementCount(character2videoModelVersion) -
          (character2videoReferencePrompts?.filter((item) => !!item && item.type !== 'video').length || 0);
        break;
      }
      case BackfillTaskType.REFERENCE2IMAGE: {
        count =
          getMaxImageAndElementCount(reference2imageFormParams.modelVersion) -
          (reference2imageReferencePrompts?.filter((item) => !!item && item.type !== 'video').length || 0);
        break;
      }
      case BackfillTaskType.IMG2VIDEO: {
        count = maxImageCount - img2videoImagePrompts?.filter((item) => !!item).length || 0;
        break;
      }
      case BackfillTaskType.SCENE: {
        const sceneImagesLimit = sceneFormParams.maxImageCount || DEFAULT_SCENE_MAX_IMAGE_COUNT;
        count = sceneImagesLimit - (sceneFormParams.imagePrompts?.filter((item) => !!item).length || 0);
        break;
      }
      case BackfillTaskType.STYLE_TRANSFER: {
        count = 1 - (styleTransferFormParams.imagePrompts?.filter((item) => !!item).length || 0);
        break;
      }
      case BackfillTaskType.FACE_SWAP: {
        count = 1 - (faceSwapFormParams.imagePrompts?.filter((item) => !!item).length || 0);
        break;
      }
      case BackfillTaskType.MOTION_CONTROL: {
        count = 1 - (motionControlFormParams.imagePrompts?.filter((item) => !!item).length || 0);
        break;
      }
      case BackfillTaskType.STORY_GRID: {
        count =
          getMaxImageAndElementCount(storyGridFormParams.modelVersion) -
          (storyGridFormParams.referencePrompts?.filter((item) => !!item && item.type !== 'video').length || 0);
        break;
      }
      default: {
        count = 0;
      }
    }

    return count > 0;
  };

  const getBackfillMaxImageSize = (backfillTaskType: BackfillTaskType) => {
    if (backfillTaskType === BackfillTaskType.CHARACTER2VIDEO) {
      return getMaxImageSize(character2videoFormParams);
    }
    if (backfillTaskType === BackfillTaskType.IMG2VIDEO) {
      return getMaxImageSize(img2videoFormParams);
    }
    if (backfillTaskType === BackfillTaskType.REFERENCE2IMAGE) {
      return getMaxImageSize(reference2imageFormParams);
    }
    if (backfillTaskType === BackfillTaskType.STYLE_TRANSFER) {
      return (
        RESOLUTION_MAP[styleTransferFormParams.settings.resolution as keyof typeof RESOLUTION_MAP] ||
        RESOLUTION_MAP['1080p']
      );
    }
    if (backfillTaskType === BackfillTaskType.FACE_SWAP) {
      return RESOLUTION_MAP['1080p'];
    }
    if (backfillTaskType === BackfillTaskType.MOTION_CONTROL) {
      return RESOLUTION_MAP[motionControlFormParams.settings.resolution as keyof typeof RESOLUTION_MAP] || 1920 * 1080;
    }
    if (backfillTaskType === BackfillTaskType.STORY_GRID) {
      return getMaxImageSize(storyGridFormParams);
    }
    return RESOLUTION_MAP[sceneFormParams.creationResolution as keyof typeof RESOLUTION_MAP] || 1920 * 1080;
  };

  useEffect(() => {
    if (pathname.includes('create/character2video')) {
      setTaskType('character2video');
    } else if (pathname.includes('create/img2video')) {
      setTaskType('img2video');
    } else if (pathname.includes('create/text2video')) {
      setTaskType('text2video');
    } else if (pathname.includes('create/reference2image')) {
      setTaskType('reference2image');
    } else if (pathname.includes('create/style-transfer')) {
      setTaskType('');
    } else if (pathname.includes('create/face-swap')) {
      setTaskType('');
    } else if (pathname.includes('create/motion-control')) {
      setTaskType('');
    } else if (pathname.includes('create/story-grid')) {
      setTaskType('');
    } else {
      setTaskType('');
    }

    const sceneMatch = pathname.match(/\/templates\/([^/]+)/);
    if (sceneMatch?.[1]) {
      setScene(sceneMatch[1]);
    } else {
      setScene('');
    }
  }, [pathname]);

  const handleImageFillData = ({
    backfillTaskType,
    imagePrompt,
    inspirationId,
    inspirationFrom,
    afterBackfill,
  }: {
    backfillTaskType: BackfillTaskType;
    imagePrompt: UploadImageType;
    inspirationId?: string;
    inspirationFrom?: string;
    /**
     * 回填完成后回调
     * @param params 回填的图片ID和图片URL
     */
    afterBackfill: (params: { id: string; src?: string }) => void;
  }) => {
    let targetIndex = 0; // 查找第一个可插入的位置
    let targetUrl = '/create/img2video'; // 默认图生页面
    if (backfillTaskType === BackfillTaskType.CHARACTER2VIDEO) {
      targetUrl = '/create/character2video';
    } else if (backfillTaskType === BackfillTaskType.REFERENCE2IMAGE) {
      targetUrl = '/create/reference2image';
    } else if (backfillTaskType === BackfillTaskType.STYLE_TRANSFER) {
      targetUrl = '/create/style-transfer';
    } else if (backfillTaskType === BackfillTaskType.FACE_SWAP) {
      targetUrl = '/create/face-swap';
    } else if (backfillTaskType === BackfillTaskType.MOTION_CONTROL) {
      targetUrl = '/create/motion-control';
    } else if (backfillTaskType === BackfillTaskType.STORY_GRID) {
      targetUrl = '/create/story-grid';
    }

    if (openInNewTab) {
      const imageData = {
        imagePrompt,
        timestamp: Date.now(),
        inspirationId,
        inspirationFrom,
      };
      safeStorage.setItem('pendingBackfillImage', JSON.stringify(imageData));
      afterBackfill({ id: 'pendingBackfillImage', src: imagePrompt.sourceImagePreview });
      openOrRedirect(targetUrl);
      return;
    }

    switch (backfillTaskType) {
      case BackfillTaskType.IMG2VIDEO: {
        const prompts = img2videoImagePrompts || [];
        const emptyIndex = prompts.findIndex((item) => !item || !item.prompt);
        targetIndex = emptyIndex !== -1 ? emptyIndex : Math.min(prompts.length, maxImageCount - 1);
        const { id } = handleBackfillImagePromptToImg2Video(imagePrompt, targetIndex);
        afterBackfill({ id, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.CHARACTER2VIDEO: {
        const nextNameIdx = getNextAvailableImageIdx(character2videoReferencePrompts || [], locale);
        const nextImageId = getBackfillImageId(nextNameIdx);
        handleBackfillImagePrompt({ imagePrompt, nextId: nextImageId });
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });

        // 如果是 character2video 任务，显示警告提示
        if (backfillTaskType === BackfillTaskType.CHARACTER2VIDEO && isProMode) {
          setChar2VideoWarningTipVisible(true);
        }
        break;
      }
      case BackfillTaskType.REFERENCE2IMAGE: {
        const nextNameIdx = getNextAvailableImageIdx(reference2imageReferencePrompts || [], locale);
        const nextImageId = getBackfillImageId(nextNameIdx);
        handleBackfillImagePromptReference2Image({ imagePrompt, nextId: nextImageId });
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.SCENE: {
        const newSceneImagePrompts = [...(sceneFormParams.imagePrompts || [])];
        const maxImageCount = sceneFormParams.maxImageCount || DEFAULT_SCENE_MAX_IMAGE_COUNT;
        const emptyIndex = newSceneImagePrompts.findIndex((item) => !item);
        const targetIndex = emptyIndex !== -1 ? emptyIndex : Math.min(newSceneImagePrompts.length, maxImageCount - 1);
        const nextImageId = getBackfillImageId(targetIndex);
        const newImagePrompt: FormImagePrompt = {
          id: nextImageId,
          status: 'done',
          prompt: imagePrompt,
        };
        newSceneImagePrompts[targetIndex] = newImagePrompt;
        updateTemplateFormParams('imagePrompts', newSceneImagePrompts);
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.STYLE_TRANSFER: {
        const nextImageId = getBackfillImageId(0);
        updateStyleTransferFormParams('imagePrompts', [
          {
            id: nextImageId,
            status: 'done',
            prompt: imagePrompt,
          },
        ]);
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.FACE_SWAP: {
        const nextImageId = getBackfillImageId(0);
        updateFaceSwapFormParams('imagePrompts', [
          {
            id: nextImageId,
            status: 'done',
            prompt: imagePrompt,
          },
        ]);
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.MOTION_CONTROL: {
        const nextImageId = getBackfillImageId(0);
        updateMotionControlFormParams('imagePrompts', [
          {
            id: nextImageId,
            status: 'done',
            prompt: imagePrompt,
          },
        ]);
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
      case BackfillTaskType.STORY_GRID: {
        const nextNameIdx = getNextAvailableImageIdx(storyGridFormParams.referencePrompts || [], locale);
        const nextImageId = getBackfillImageId(nextNameIdx);
        const nextImageName = getNextImageName(nextNameIdx, locale);
        updateFormParams('storyGrid', 'referencePrompts', [
          ...(storyGridFormParams.referencePrompts || []),
          {
            id: nextImageId,
            type: 'image',
            name: nextImageName,
            status: 'done',
            prompt: imagePrompt,
          },
        ]);
        afterBackfill({ id: nextImageId, src: imagePrompt.sourceImagePreview });
        break;
      }
    }

    router.push(targetUrl);

    toast({
      type: 'success',
      message: t('frameShotDialog.backfillSuccess'),
    });
  };

  /**
   * 回填参考图 通过canvas
   * @param canvas 截图canvas
   * @param backfillTaskType 回填任务类型
   */
  const handleBackfillImage = ({
    canvas,
    backfillTaskType,
    afterBackfill,
  }: {
    canvas: HTMLCanvasElement;
    backfillTaskType: BackfillTaskType;
    afterBackfill: (params: { id: string; src?: string }) => void;
  }) => {
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;

    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          toast({
            type: 'error',
            message: '获取截图失败',
          });
          return;
        }

        try {
          const previewUrl = URL.createObjectURL(blob);
          const [{ uri, error: sourceImageError }, resizeImageResult] = await Promise.all([
            uploadFile({ file: blob, width: canvas.width, height: canvas.height }),
            resizeAndUploadImage({
              url: previewUrl,
              maxImageSize: getBackfillMaxImageSize(backfillTaskType),
            }),
          ]);

          if (sourceImageError || !resizeImageResult || resizeImageResult.error) {
            throw new Error('backfillImageError');
          }
          const { error: _, ...resizeImage } = resizeImageResult;

          const { width: sourceImageWidth, height: sourceImageHeight } = canvas;
          const imagePrompt: UploadImageType = {
            sourceImage: uri, // 使用服务器返回的URI
            sourceImagePreview: previewUrl, // 本地预览使用Blob URL
            sourceImageWidth,
            sourceImageHeight,
            selectedImageRange: [0, 0, sourceImageWidth, sourceImageHeight],
            ...resizeImage,
          };

          handleImageFillData({ backfillTaskType, imagePrompt, afterBackfill });
        } catch (error) {
          console.error('回填截图失败:', error);
          toast({
            type: 'error',
            message: t('frameShotDialog.backfillFailed'),
          });
        }
      },
      'image/webp',
      WEBP_BACKFILL_QUALITY,
    );
  };

  /**
   * 回填参考图 通过url
   * @param param0 回填任务类型，图片url，图片宽度，图片高度，灵感ID，灵感来源
   */
  const handleBackfillImageByUrl = ({
    backfillTaskType,
    src,
    width = 0,
    height = 0,
    inspirationId,
    inspirationFrom,
    afterBackfill,
  }: {
    backfillTaskType: BackfillTaskType;
    src: string;
    width?: number;
    height?: number;
    inspirationId?: string;
    inspirationFrom?: string;
    afterBackfill: (params: { id: string; src?: string }) => void;
  }) => {
    handleImageFillData({
      backfillTaskType,
      imagePrompt: {
        sourceImage: src,
        sourceImageWidth: width,
        sourceImageHeight: height,
        selectedImageRange: [0, 0, width, height],
        sourceImagePreview: src,
        selectedImage: src,
        selectedImagePreview: src,
      },
      inspirationId,
      inspirationFrom,
      afterBackfill,
    });
  };

  return { taskType, getBackfillAvailable, handleBackfillImage, handleBackfillImageByUrl, scene };
};
